/* global SideDrawer */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.expanded = [];
      this.filterChangeTimeout = null;

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();

      // Custom enhancements
      this.initYearRangeControls();
      this.initMakeDropdown();
      this.initMakeModelLock();

      // NEW: Make-scoped models using /collections/<make-handle>
      this.initMakeScopedModels();

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll('.js-drawer-sort-by');
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }
    }

    addListeners() {
      if (this.filteringEnabled) {
        this.breakpointChangeHandler = this.breakpointChangeHandler
          || this.handleBreakpointChange.bind(this);
        this.filters.addEventListener('click', this.handleFiltersClick.bind(this));
        this.filters.addEventListener('input', this.handleFilterChange.bind(this));
        this.filters.addEventListener('change', this.handleFilterChange.bind(this));
        this.activeFilters?.addEventListener('click', this.handleActiveFiltersClick.bind(this));
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }

      if (this.sortingEnabled && this.desktopSortBy) {
        this.desktopSortBy.addEventListener('change', this.handleFilterChange.bind(this));
      }
    }

    /* =========================
       Helpers (NEW)
       ========================= */
    slugifyHandle(str) {
      return String(str || '')
        .trim()
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    async collectionExists(path) {
      try {
        const res = await fetch(path, { method: 'HEAD', credentials: 'same-origin' });
        return res.ok;
      } catch {
        return false;
      }
    }

    async fetchCollectionHtml(url) {
      // Use a collection page and ask for a well-known section first (fast + consistent)
      const trySectionIds = ['main-collection-product-grid', 'main-collection', 'collection-main'];

      for (const sid of trySectionIds) {
        try {
          const u = url + (url.includes('?') ? '&' : '?') + `section_id=${encodeURIComponent(sid)}`;
          const res = await fetch(u, { credentials: 'same-origin' });
          if (!res.ok) continue;
          const html = await res.text();
          if (html && html.length > 100) return html;
        } catch (_) {}
      }

      // Fallback: full page
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`Failed to fetch: ${url}`);
      return await res.text();
    }

    extractFacetValues(html, key) {
      if (!key) return [];
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // IMPORTANT: include disabled values too, so new models never get dropped.
      const inputs = Array.from(doc.querySelectorAll(`input[name="${CSS.escape(key)}"]`));
      const values = inputs.map((i) => i.value).filter(Boolean);

      const uniq = Array.from(new Set(values));
      uniq.sort((a, b) => a.localeCompare(b));
      return uniq;
    }

    /* =========================
       Year Range (Apply only)
       ========================= */
    initYearRangeControls() {
      const blocks = this.querySelectorAll('[data-year-range]');
      if (!blocks.length) return;

      blocks.forEach((block) => {
        if (block.dataset.bound === 'true') return;
        block.dataset.bound = 'true';

        const minInput = block.querySelector('[data-year-min]');
        const maxInput = block.querySelector('[data-year-max]');
        const applyBtn = block.querySelector('[data-year-apply]');
        const clearBtn = block.querySelector('[data-year-clear]');

        const minRange = block.querySelector('[data-year-min-range]');
        const maxRange = block.querySelector('[data-year-max-range]');
        const minLabel = block.querySelector('[data-year-min-label]');
        const maxLabel = block.querySelector('[data-year-max-label]');
        const fill = block.querySelector('[data-year-fill]');

        const yearChecks = Array.from(block.querySelectorAll('input[type="checkbox"][data-year-value]'));

        const parseYear = (v) => {
          const n = Number.parseInt(String(v ?? '').trim(), 10);
          return Number.isFinite(n) ? n : null;
        };

        const years = yearChecks
          .map((c) => parseYear(c.dataset.yearValue))
          .filter((n) => n !== null)
          .sort((a, b) => a - b);

        if (!years.length) return;

        const minYear = years[0];
        const maxYear = years[years.length - 1];

        const snapToExisting = (y) => {
          const yy = Math.min(maxYear, Math.max(minYear, y));
          let closest = years[0];
          let best = Math.abs(yy - closest);
          for (const v of years) {
            const d = Math.abs(yy - v);
            if (d < best) { best = d; closest = v; }
          }
          return closest;
        };

        const setLabels = (low, high) => {
          if (minLabel) minLabel.textContent = String(low);
          if (maxLabel) maxLabel.textContent = String(high);
        };

        const setFill = (low, high) => {
          if (!fill) return;
          const left = ((low - minYear) / (maxYear - minYear)) * 100;
          const right = ((high - minYear) / (maxYear - minYear)) * 100;
          fill.style.left = `${left}%`;
          fill.style.width = `${Math.max(0, right - left)}%`;
        };

        const setUI = (low, high, setInputs = true) => {
          if (minRange) minRange.value = String(low);
          if (maxRange) maxRange.value = String(high);
          if (setInputs) {
            if (minInput) minInput.value = String(low);
            if (maxInput) maxInput.value = String(high);
          }
          setLabels(low, high);
          setFill(low, high);
        };

        if (minInput) { minInput.min = String(minYear); minInput.max = String(maxYear); minInput.placeholder = String(minYear); }
        if (maxInput) { maxInput.min = String(minYear); maxInput.max = String(maxYear); maxInput.placeholder = String(maxYear); }
        if (minRange) { minRange.min = String(minYear); minRange.max = String(maxYear); }
        if (maxRange) { maxRange.min = String(minYear); maxRange.max = String(maxYear); }

        const activeYears = yearChecks
          .filter((c) => c.checked)
          .map((c) => parseYear(c.dataset.yearValue))
          .filter((n) => n !== null)
          .sort((a, b) => a - b);

        if (activeYears.length) {
          setUI(activeYears[0], activeYears[activeYears.length - 1], true);
        } else {
          setUI(minYear, maxYear, false);
        }

        const getLowHighFromUI = () => {
          const a = snapToExisting(parseYear(minRange?.value) ?? minYear);
          const b = snapToExisting(parseYear(maxRange?.value) ?? maxYear);
          return { low: Math.min(a, b), high: Math.max(a, b) };
        };

        const syncFromSliders = () => {
          const { low, high } = getLowHighFromUI();
          setUI(low, high, true);
        };

        const syncFromInputs = () => {
          const a0 = parseYear(minInput?.value);
          const b0 = parseYear(maxInput?.value);
          if (a0 === null && b0 === null) return;

          const a = snapToExisting(a0 ?? b0);
          const b = snapToExisting(b0 ?? a0);
          const low = Math.min(a, b);
          const high = Math.max(a, b);
          setUI(low, high, true);
        };

        const triggerApply = () => {
          const any = yearChecks.find((c) => !c.disabled) || yearChecks[0];
          if (!any) return;
          any.dataset.yearRangeTrigger = '1';
          any.dispatchEvent(new Event('input', { bubbles: true }));
          delete any.dataset.yearRangeTrigger;
        };

        const applyRange = () => {
          const { low, high } = getLowHighFromUI();

          yearChecks.forEach((c) => {
            if (c.disabled) return;
            const y = parseYear(c.dataset.yearValue);
            if (y === null) return;
            c.checked = y >= low && y <= high;
          });

          triggerApply();
        };

        const clearRange = () => {
          yearChecks.forEach((c) => {
            if (c.disabled) return;
            c.checked = false;
          });

          setUI(minYear, maxYear, false);
          if (minInput) minInput.value = '';
          if (maxInput) maxInput.value = '';
          triggerApply();
        };

        minRange?.addEventListener('input', syncFromSliders);
        maxRange?.addEventListener('input', syncFromSliders);

        minInput?.addEventListener('input', syncFromInputs);
        maxInput?.addEventListener('input', syncFromInputs);

        applyBtn?.addEventListener('click', applyRange);
        clearBtn?.addEventListener('click', clearRange);

        [minInput, maxInput].forEach((el) => {
          el?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              applyRange();
            }
          });
        });
      });
    }

    /* =========================
       Make dropdown (single select)
       ========================= */
    initMakeDropdown() {
      const blocks = this.querySelectorAll('[data-make-select]');
      if (!blocks.length) return;

      blocks.forEach((block) => {
        if (block.dataset.bound === 'true') return;
        block.dataset.bound = 'true';

        const select = block.querySelector('[data-make-select-input]');
        const checks = Array.from(block.querySelectorAll('[data-make-values] input[type="checkbox"]'));
        if (!select || !checks.length) return;

        const applySelect = () => {
          const val = select.value;

          checks.forEach((c) => { c.checked = false; });

          if (val) {
            const match = checks.find((c) => c.value === val);
            if (match && !match.disabled) match.checked = true;
          }

          const trigger = checks[0];
          if (trigger) trigger.dispatchEvent(new Event('input', { bubbles: true }));
        };

        select.addEventListener('change', applySelect);
      });
    }

    /* =========================
       Lock model until make selected (UX lock)
       ========================= */
    initMakeModelLock() {
      const makeFilter = this.querySelector('details[data-filter-make]');
      const modelFilter = this.querySelector('details[data-filter-model][data-lock-until-make]');
      if (!makeFilter || !modelFilter) return;

      const makeChecks = makeFilter.querySelectorAll('input[type="checkbox"], input[type="radio"]');
      const modelChecks = modelFilter.querySelectorAll('input[type="checkbox"], input[type="radio"]');

      const isMakeSelected = () => Array.from(makeChecks).some((i) => i.checked);

      modelChecks.forEach((i) => {
        if (i.disabled) i.dataset.originalDisabled = 'true';
      });

      const applyLockState = () => {
        const locked = !isMakeSelected();
        modelFilter.classList.toggle('is-locked', locked);
        modelFilter.classList.toggle('hide-options', locked);

        modelChecks.forEach((i) => {
          if (i.checked) {
            i.disabled = false;
          } else {
            i.disabled = locked ? true : (i.dataset.originalDisabled === 'true');
          }
        });

        let hint = modelFilter.querySelector('.model-lock-hint');
        if (locked) {
          if (!hint) {
            hint = document.createElement('div');
            hint.className = 'model-lock-hint';
            hint.textContent = 'Select a Make first to choose a Model.';
            const content = modelFilter.querySelector('.filter__content');
            if (content) content.prepend(hint);
          }
        } else if (hint) {
          hint.remove();
        }
      };

      applyLockState();
      makeFilter.addEventListener('input', applyLockState);
      makeFilter.addEventListener('change', applyLockState);
    }

    /* =========================
       NEW: Make-scoped Model list on /collections/all
       ========================= */
    initMakeScopedModels() {
      const makeFilter = this.querySelector('details[data-filter-make]');
      const modelFilter = this.querySelector('details[data-filter-model]');
      if (!makeFilter || !modelFilter) return;

      const makeSelect = makeFilter.querySelector('[data-make-select-input]');
      if (!makeSelect) return;

      // This UL must exist; we add data-model-values-list in facet-filters.liquid
      const modelList = modelFilter.querySelector('ul[data-model-values-list]');
      if (!modelList) return;

      // Detect model key from any checkbox in model filter
      const firstModelInput = modelFilter.querySelector('input[type="checkbox"][name^="filter."]');
      const modelKey = firstModelInput?.getAttribute('name');
      if (!modelKey) return;

      const rebuildModelList = (values) => {
        const checked = new Set(
          Array.from(modelFilter.querySelectorAll(`input[name="${CSS.escape(modelKey)}"]:checked`)).map((i) => i.value)
        );

        modelList.innerHTML = '';

        values.forEach((val, idx) => {
          const li = document.createElement('li');

          const safeKey = modelKey.replace(/[^a-z0-9_-]/gi, '_');
          const id = `filter-${safeKey}-scoped-${idx + 1}`;

          const input = document.createElement('input');
          input.type = 'checkbox';
          input.className = 'checkbox visually-hidden focus-label-before';
          input.id = id;
          input.name = modelKey;
          input.value = val;
          if (checked.has(val)) input.checked = true;

          const label = document.createElement('label');
          label.className = 'filter__label flex items-start w-full m-0';
          label.setAttribute('for', id);

          const span = document.createElement('span');
          span.className = 'flex-auto';
          span.textContent = val;

          label.appendChild(span);
          li.appendChild(input);
          li.appendChild(label);
          modelList.appendChild(li);
        });

        // If we rebuild, remove "show more" since we control full list now
        modelFilter.querySelector('.js-show-more')?.remove();
        modelFilter.querySelector('.filter__more')?.remove();
      };

      const applyScopedModels = async () => {
        const make = makeSelect.value;
        if (!make) return;

        const handle = this.slugifyHandle(make);
        const makeCollection = `/collections/${handle}`;

        if (!(await this.collectionExists(makeCollection))) return;

        const html = await this.fetchCollectionHtml(makeCollection);
        const models = this.extractFacetValues(html, modelKey);

        if (models && models.length) rebuildModelList(models);
      };

      // Bind only once per render
      if (makeSelect.dataset.modelScopeBound === 'true') return;
      makeSelect.dataset.modelScopeBound = 'true';

      makeSelect.addEventListener('change', () => {
        applyScopedModels().catch(() => {});
      });

      // If already selected (e.g. back button), apply immediately
      if (makeSelect.value) {
        applyScopedModels().catch(() => {});
      }
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
      } else {
        this.close();
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
      }
    }

    handleFilterChange(evt) {
      if (evt.target && evt.target.closest?.('[data-year-range]')) {
        const forced = evt.target.dataset?.yearRangeTrigger === '1';
        if (!forced) return;
      }

      if (evt.type === 'change' && !(evt.target.id?.includes('price-range') || evt.target.id?.includes('sort-by'))) return;
      if (evt.target.id?.includes('price-range') && evt.constructor.name === 'InputEvent') return;

      const timeoutDelay = 500;

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams(formData);
        const emptyParams = [];

        if (this.sortingEnabled) {
          let currentSortBy = searchParams.get('sort_by');

          if (evt.target.tagName === 'CUSTOM-SELECT') {
            this.mobileSortByOptions.forEach((option) => {
              option.checked = option.value === evt.detail.selectedValue;
              currentSortBy = evt.detail.selectedValue;
            });
          }

          searchParams.set('sort_by', currentSortBy);
        }

        searchParams.forEach((value, key) => {
          if (!value) emptyParams.push(key);
        });

        emptyParams.forEach((key) => {
          searchParams.delete(key);
        });

        this.applyFilters(searchParams.toString(), evt);
      }, timeoutDelay);
    }

    handleFiltersClick(evt) {
      const { target } = evt;

      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });

        if (!this.expanded.includes(filter.id)) {
          this.expanded.push(filter.id);
        }
      }
    }

    handleActiveFiltersClick(evt) {
      if (evt.target.tagName !== 'A') return;
      evt.preventDefault();
      this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
    }

    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    async applyFilters(searchParams, evt, updateUrl = true) {
      try {
        const activeElementId = document.activeElement.id;

        const customPagination = document.querySelector('custom-pagination');
        if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

        this.results.classList.add('is-loading');

        const closeBtn = this.querySelector('.js-close-drawer-mob');
        closeBtn.ariaDisabled = 'true';
        closeBtn.classList.add('is-loading');

        let fetchUrl = `${window.location.pathname}?${searchParams}`;
        if (this.form.dataset.filterSectionId) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort('Request changed');
        }
        this.applyFiltersFetchAbortController = new AbortController();

        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.applyFiltersFetchAbortController.signal
        });

        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) target.open = existingFilter.open;
          });

          tmpl.content.querySelectorAll('#facets details-disclosure > details').forEach((newFilter) => {
            if (this.expanded.includes(newFilter.id)) {
              const hiddenElements = newFilter.querySelectorAll('.js-hidden');
              hiddenElements.forEach((listItem) => {
                listItem.classList.remove('js-hidden');
              });
              newFilter.querySelector('.filter__more')?.remove();
            }
          });

          this.form.innerHTML = tmpl.content.getElementById('facets').innerHTML;

          closeBtn.innerText = tmpl.content.querySelector('.js-close-drawer-mob').innerText;

          const currentResultsUl = this.results.querySelector('ul');
          this.currentResultsClass = currentResultsUl ? currentResultsUl.getAttribute('class') : this.currentResultsClass;

          this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl && this.currentResultsClass) newResultsUl.setAttribute('class', this.currentResultsClass);

          this.addElements();
          this.addListeners();

          this.initYearRangeControls();
          this.initMakeDropdown();
          this.initMakeModelLock();
          this.initMakeScopedModels(); // NEW: reapply after rerender

          if (customPagination && customPagination.reload) customPagination.reload();

          if (updateUrl) FacetFilters.updateURL(searchParams);

          if (this.results.getBoundingClientRect().top < 0) {
            let headerHeight = 0;
            if (document.querySelector('store-header[data-is-sticky="true"]')) {
              headerHeight = Number.parseInt(
                getComputedStyle(this.parentElement)
                  .getPropertyValue('--header-height')
                  .replace('px', ''),
                10
              ) || 0;
            }
            window.scrollTo({
              top: this.results.getBoundingClientRect().top + window.scrollY - headerHeight - 45,
              behavior: 'smooth'
            });
          }

          closeBtn.classList.remove('is-loading');
          closeBtn.removeAttribute('aria-disabled');

          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          if (activeElementId) document.getElementById(activeElementId)?.focus();

          document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });
        }
      } catch (error) {
        console.warn(error); // eslint-disable-line
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    static updateURL(searchParams) {
      window.history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
      );
    }
  }

  customElements.define('facet-filters', FacetFilters);
}
